import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, MobileStepper as Stepper } from "@mui/material";
import Shell from "components/shell";
import { useEnrollmentSteps } from "hooks/useEnrollmentSteps";
import { useEnrollmentForm } from "hooks/useEnrollmentForm";
import {
    ENROLLMENT_ERROR_PAGE,
    ENROLLMENT_STEPS_NUMBER,
    ENROLLMENT_USER_AUTH_DATA,
} from "constants/enrollment";
import { isOver18 } from "utilities";

import {
    accountCreated,
    addShippingAddress,
    checkEligibility,
    createAccount,
    getSponsorRules,
    programDetermination,
    verifyShippingAddress,
} from "data";

import { formSections } from "./formSections";
import StepRenderer from "./StepRenderer";
import {
    buildEligibilityPayload,
    buildProgramPayload,
    buildAccountPayload,
    buildShippingAddress,
} from "./apiPayloadUtils";

const StepEnrollmentForm = () => {
    const params = useParams();
    const SPONSOR_ID = params.sponsor.toUpperCase();
    const { formData, handleFormChange, resetForm, onSubmit, isSubmitting } =
        useEnrollmentForm(formSections);
    const {
        activeStep,
        setActiveStep,
        errorScreen,
        setErrorScreen,
        resetToInitialStep,
    } = useEnrollmentSteps(ENROLLMENT_STEPS_NUMBER.ELIGIBILITY_CHECK);

    useEffect(() => {
        fetchSponsorRules();
        const authData = JSON.parse(localStorage.getItem(ENROLLMENT_USER_AUTH_DATA) || null);
        if (authData) {
            handleFormChange("internalFormData", "authToken", authData.auth_token);
            handleFormChange("shipping", "firstName", authData.firstName);
            handleFormChange("shipping", "lastName", authData.lastName);
            //remove the auth token from local storage after storing it in the form data.
            localStorage.removeItem(ENROLLMENT_USER_AUTH_DATA);
            setActiveStep(ENROLLMENT_STEPS_NUMBER.SHIPPING_ADDRESS);
        }
    }, []);

    const handleFormReset = () => {
        resetForm();
        // Get the sponsor rules again on form reset to populate the dropdowns with correct options.
        fetchSponsorRules();
    };

    // Fetch sponsor rules
    const fetchSponsorRules = async () => {
        const response = await getSponsorRules(SPONSOR_ID);
        if (!response.ok) {
            throw new Error("Failed to fetch sponsor rules");
        }
        const rules = await response.json();

        const treatQuestionOption = rules.treatQuestionOption.map((item) => ({
            label: item.title,
            value: item.key,
        }));
        const preventQuestionOption = rules.preventQuestionOption.map((item) => ({
            label: item.title,
            value: item.key,
        }));

        handleFormChange(
            "internalFormData",
            "treatQuestionOption",
            treatQuestionOption,
        );
        handleFormChange(
            "internalFormData",
            "preventQuestionOption",
            preventQuestionOption,
        );
    };

    // Handle eligibility check
    const handleCheckEligibility = async () => {
        if (!isOver18(formData.eligibility.dateOfBirth)) {
            setErrorScreen(ENROLLMENT_ERROR_PAGE.UNDER_AGE);
            return;
        }
        const payload = buildEligibilityPayload(SPONSOR_ID, formData);
        try {
            const response = await onSubmit(() =>
                checkEligibility(SPONSOR_ID, payload),
            );
            const result = await response.json();
            handleFormChange(
                "internalFormData",
                "eligibleId",
                result?.eligibleId || null,
            );
            if (result?.eligible) {
                setActiveStep(ENROLLMENT_STEPS_NUMBER.ELIGIBILITY_SUCCESS);
            } else if (!result?.eligible) {
                setActiveStep(ENROLLMENT_STEPS_NUMBER.ELIGIBILITY_MORE_INFO);
            }
        } catch (error) {
            setErrorScreen(ENROLLMENT_ERROR_PAGE.DEFAULT);
        }
    };

    // Handle eligibility check
    const handleCheckEligibilityMoreInfo = async () => {
        const payload = buildEligibilityPayload(SPONSOR_ID, formData);
        try {
            const response = await onSubmit(() =>
                checkEligibility(SPONSOR_ID, payload),
            );
            const result = await response.json();
            handleFormChange(
                "internalFormData",
                "eligibleId",
                result?.eligibleId || null,
            );

            if (result?.eligible) {
                setActiveStep(ENROLLMENT_STEPS_NUMBER.ELIGIBILITY_SUCCESS);
            } else if (!result?.eligible) {
                setErrorScreen(ENROLLMENT_ERROR_PAGE.DEFAULT);
            }
        } catch (error) {
            setErrorScreen(ENROLLMENT_ERROR_PAGE.DEFAULT);
        }
    };

    const handleEligibilitySuccessNext = () =>
        setActiveStep(ENROLLMENT_STEPS_NUMBER.CARE_PATHWAY_TREAT);

    const handleTreatNext = () =>
        setActiveStep(ENROLLMENT_STEPS_NUMBER.CARE_PATHWAY_PREVENT);

    const handlePreventSubmit = async () => {
        const payload = buildProgramPayload(formData);
        try {
            const response = await onSubmit(() =>
                programDetermination(SPONSOR_ID, payload),
            );
            const result = await response.json();
            handleFormChange("internalFormData", "programId", result?.programId);
            setActiveStep(ENROLLMENT_STEPS_NUMBER.PROGRAM_RESULT);
        } catch { }
    };

    const handleProgramContinue = () =>
        setActiveStep(ENROLLMENT_STEPS_NUMBER.CREATE_ACCOUNT);

    const handleCreateAccount = async () => {
        const payload = buildAccountPayload(SPONSOR_ID, formData);
        try {
            const response = await onSubmit(() => createAccount(payload));
            const data = await response.json();
            if (data.errors) {
                return Promise.reject(data.errors);
            }
            //If account is created  call account created API.
            accountCreated(data?.authToken, data.userId);

            handleFormChange(
                "internalFormData",
                "authToken",
                data?.authToken || null,
            );
            handleFormChange("internalFormData", "userId", data?.userId || null);

            //prepopulate shipping address with the data from account creation response.
            handleFormChange(
                "shipping",
                "firstName",
                data?.firstName || null,
            );
            handleFormChange(
                "shipping",
                "lastName",
                data?.lastName || null,
            );
            handleFormChange(
                "shipping",
                "address1",
                data?.address1 || null,
            );
            handleFormChange(
                "shipping",
                "address2",
                data?.address2 || null,
            );
            handleFormChange(
                "shipping",
                "city",
                data?.city || null,
            );
            handleFormChange(
                "shipping",
                "state",
                data?.state || null,
            );
            handleFormChange(
                "shipping",
                "postalCode",
                data?.postalCode || null,
            );

            setActiveStep(ENROLLMENT_STEPS_NUMBER.SHIPPING_ADDRESS);
        } catch (errorData) {
            return Promise.reject(errorData);
        }
    };

    const handleMigrateUser = async () => {
        //TODO: Implement migrate user API call here.
        alert("Migrate user functionality comming soon.");
    };

    const handleVerifyShippingAddress = async () => {
        const currentAddress = buildShippingAddress(formData);
        return onSubmit(() =>
            verifyShippingAddress(
                currentAddress,
                formData.internalFormData.authToken,
            ),
        );
    };

    const handleAddShippingAddress = async (address) => {
        return onSubmit(() =>
            addShippingAddress(address, formData.internalFormData.authToken),
        ).then(() => {
            setActiveStep(ENROLLMENT_STEPS_NUMBER.SUCCESS);
        });
    };

    const handleLogin = () => {
        window.location.href = "/login";
    };

    const getStepProgress = () => {
        if (errorScreen) {
            return 0;
        }
        // If user is in program result step but doesn't have a program, show progress as if they are at the end of the steps since they can only continue to create account or end the flow
        else if (
            activeStep == ENROLLMENT_STEPS_NUMBER.PROGRAM_RESULT &&
            !formData.internalFormData.programId
        ) {
            return Object.keys(ENROLLMENT_STEPS_NUMBER).length - 1;
        }
        return activeStep;
    };

    return (
        <Shell
            confirmation={activeStep === ENROLLMENT_STEPS_NUMBER.SUCCESS}
            Stepper={
                <Box sx={{ width: "100%", mb: 2 }}>
                    <Stepper
                        variant="progress"
                        steps={Object.keys(ENROLLMENT_STEPS_NUMBER).length}
                        position="static"
                        activeStep={getStepProgress()}
                        sx={{
                            width: "100%",
                            flexGrow: 1,
                            bgcolor: "transparent",
                            boxShadow: "none",
                            p: 0,
                            "& .MuiMobileStepper-progress": {
                                width: "100%",
                            },
                            "& .MuiLinearProgress-bar": {
                                bgcolor: errorScreen ? "error.main" : "primary.main",
                            },
                        }}
                    />
                </Box>
            }
        >
            <StepRenderer
                step={activeStep}
                formData={formData}
                errorScreen={errorScreen}
                resetToInitialStep={() => resetToInitialStep(handleFormReset)}
                isSubmitting={isSubmitting}
                handleFormChange={handleFormChange}
                // Pass all the handlers to StepRenderer
                handleCheckEligibility={handleCheckEligibility}
                handleCheckEligibilityMoreInfo={handleCheckEligibilityMoreInfo}
                handleEligibilitySuccessNext={handleEligibilitySuccessNext}
                handleTreatNext={handleTreatNext}
                handlePreventSubmit={handlePreventSubmit}
                handleProgramContinue={handleProgramContinue}
                handleMigrateUser={handleMigrateUser}
                handleCreateAccount={handleCreateAccount}
                handleAddShippingAddress={handleAddShippingAddress}
                handleVerifyShippingAddress={handleVerifyShippingAddress}
            />
        </Shell>
    );
};

export default StepEnrollmentForm;
